"use client";

import Link from "next/link";
import { Suspense, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { useDispatch } from "react-redux";

import { setAffiliation } from "@/features/affiliate/affiliateSlice";
import {
	AUTH_CARD_HEADER_TOP_SIGNUP_ENTRY_PX,
	AUTH_SIGNUP_CARD_DESCRIPTION,
} from "@/lib/auth-card-copy";
import { OAUTH_APPLE_ENDPOINT, OAUTH_GOOGLE_ENDPOINT } from "@/lib/constant";
import { rememberDetectedCountryForAuth } from "@/lib/ip-geolocation";
import { cn } from "@/lib/utils";
import { getClientFrontendOrigin } from "@/lib/utils/frontend-origin";

import AuthProviderButton from "@/components/AuthProviderButton";
import { useToast } from "@/components/ui/use-toast";
import { FONT_SIZES } from "@/components/ui/constants";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import AppleIcon from "@mui/icons-material/Apple";

import googleIcon from "/public/assets/login/login-form/google.png";
import EmailIcon from "@/components/icons/EmailIcon";
import { ga4CtaClick } from "@/lib/analytics/ga4-ecommerce";

type SignupEntryInnerProps = {
	onSwitchToLogin?: () => void;
	onSwitchToEmailSignup?: () => void;
	returnUrl?: string;
	showOneClickSeparator?: boolean;
	glassmorphic?: boolean;
	hideTitle?: boolean;
	description?: string;
};

function SignupEntryInner({
	onSwitchToLogin,
	onSwitchToEmailSignup,
	returnUrl,
	showOneClickSeparator = false,
	glassmorphic = false,
	hideTitle = false,
	description = AUTH_SIGNUP_CARD_DESCRIPTION,
}: SignupEntryInnerProps) {
	const dispatch = useDispatch();
	const router = useRouter();
	const { toast } = useToast();
	const { data: session, status } = useSession();
	const searchParams = useSearchParams();
	const ref = searchParams.get("ref");
	const isMainSignupPage = !onSwitchToLogin && !onSwitchToEmailSignup;
	const encodedReturnUrl = returnUrl ? encodeURIComponent(returnUrl) : null;
	const signupEmailHref = encodedReturnUrl
		? `/signup/email?returnUrl=${encodedReturnUrl}`
		: "/signup/email";
	const loginHref = encodedReturnUrl ? `/login?returnUrl=${encodedReturnUrl}` : "/login";
	const glassButtonClass = glassmorphic
		? "!border-[color:var(--glass-border)] !bg-[rgba(255,255,255,0.18)] !shadow-[0_8px_24px_rgba(7,168,188,0.12)] backdrop-blur-xl [-webkit-backdrop-filter:var(--glass-backdrop-filter)] [backdrop-filter:var(--glass-backdrop-filter)] hover:!border-tertiary/60 hover:!bg-tertiary hover:!text-white dark:!bg-[rgba(11,21,27,0.35)] dark:hover:!bg-tertiary"
		: undefined;

	useEffect(() => {
		if (ref) dispatch(setAffiliation(ref));
	}, [dispatch, ref]);

	useEffect(() => {
		if (isMainSignupPage && status === "authenticated" && session) {
			router.replace("/");
		}
	}, [isMainSignupPage, router, session, status]);

	if (isMainSignupPage && status === "authenticated") {
		return null;
	}

	const handleGoogleAuth = async () => {
		const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

		if (!clientId || clientId.includes("REPLACE") || clientId.includes("REPLACE_WITH")) {
			console.warn("Google Client ID not configured. Google OAuth is disabled for local development.");
			toast({
				title: "Google OAuth not available",
				description: "Google OAuth is not configured. Continue with email or Apple.",
				variant: "default",
			});
			return;
		}

		ga4CtaClick({
			cta_id: "signup_google",
			cta_name: "Continue with Google",
			cta_location: "signup_entry",
		});

		const baseUrl = getClientFrontendOrigin();
		const redirectUri = `${baseUrl}/signup/google`;
		const oauthState = returnUrl || "/";

		const oauthParams = new URLSearchParams({
			client_id: clientId,
			redirect_uri: redirectUri,
			response_type: "code",
			scope: "openid email profile",
			access_type: "online",
			prompt: "consent",
			state: oauthState,
		});

		const oauthUrl = `${OAUTH_GOOGLE_ENDPOINT}?${oauthParams.toString()}`;

		await rememberDetectedCountryForAuth();
		window.location.href = oauthUrl;
	};

	const handleAppleAuth = async () => {
		const clientId = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID;

		if (!clientId || clientId.includes("REPLACE") || clientId.includes("REPLACE_WITH")) {
			toast({
				title: "Apple Sign In not available",
				description: "Apple Sign In is not configured. Continue with email or Google.",
				variant: "default",
			});
			return;
		}

		ga4CtaClick({
			cta_id: "signup_apple",
			cta_name: "Continue with Apple",
			cta_location: "signup_entry",
		});

		const baseUrl = getClientFrontendOrigin();
		const redirectUri = `${baseUrl}/signup/apple`;
		const oauthState = returnUrl || "/";

		const oauthParams = new URLSearchParams({
			client_id: clientId,
			redirect_uri: redirectUri,
			response_type: "code id_token",
			response_mode: "form_post",
			scope: "name email",
			state: oauthState,
		});

		await rememberDetectedCountryForAuth();
		window.location.href = `${OAUTH_APPLE_ENDPOINT}?${oauthParams.toString()}`;
	};

	const loginControl = onSwitchToLogin ? (
		<button
			type="button"
			onClick={onSwitchToLogin}
			className="text-accent-foreground underline"
		>
			Log in
		</button>
	) : (
		<Link className="text-accent-foreground underline" href={loginHref}>
			Log in
		</Link>
	);

	return (
		<Card
			className={cn(
				"w-full max-w-md rounded-3xl px-1 sm:px-6",
				glassmorphic
					? "glassmorphism-card border border-[color:var(--glass-border)] bg-[var(--glass-background)] shadow-[var(--glass-shadow)] backdrop-blur-xl [-webkit-backdrop-filter:var(--glass-backdrop-filter)] [backdrop-filter:var(--glass-backdrop-filter)]"
					: "border-none !shadow-custom",
			)}
		>
			<CardHeader className="flex flex-col gap-[14px] space-y-0 px-8 pb-0 pt-0">
				{hideTitle ? null : (
					<CardTitle
						className="text-xl font-semibold leading-snug xs:text-2xl sm:text-2xl md:text-3xl"
						style={{ paddingTop: AUTH_CARD_HEADER_TOP_SIGNUP_ENTRY_PX }}
					>
						Explore
						<span className="text-accent-foreground"> new horizons</span> <br />
						with us
					</CardTitle>
				)}
				<CardDescription className={cn("mt-0 pt-0", hideTitle && "pt-8 text-center text-sm font-medium")}>
					{description}
				</CardDescription>
			</CardHeader>
			<CardContent className="px-8 pb-6 pt-[20px]">
				<div className="flex flex-col gap-y-3 pb-4 pt-0">
					{onSwitchToEmailSignup ? (
						<button
							type="button"
							onClick={() => {
								ga4CtaClick({
									cta_id: "signup_email",
									cta_name: "Continue with Email",
									cta_location: "signup_entry",
								});
								onSwitchToEmailSignup();
							}}
							className={cn(
								`flex h-11 w-full items-center gap-x-2 ${FONT_SIZES.authButton} justify-center rounded-sm border border-secondary/10 bg-card px-4 text-card-foreground hover:bg-accent hover:text-accent-foreground dark:border-border dark:bg-card dark:text-card-foreground dark:hover:bg-accent dark:hover:text-accent-foreground xs:gap-x-3 xs:px-10`,
								glassButtonClass,
							)}
						>
							<div className="flex items-center justify-center">
								<EmailIcon className="h-auto w-8" />
							</div>
							<span className="text-nowrap font-normal">Continue with Email</span>
						</button>
					) : (
						<Link
							href={signupEmailHref}
							onClick={() =>
								ga4CtaClick({
									cta_id: "signup_email",
									cta_name: "Continue with Email",
									cta_location: "signup_entry",
									link_url: signupEmailHref,
								})
							}
							className={cn(
								`flex h-11 w-full items-center gap-x-2 ${FONT_SIZES.authButton} justify-center rounded-sm border border-secondary/10 bg-card px-4 text-card-foreground hover:bg-accent hover:text-accent-foreground dark:border-border dark:bg-card dark:text-card-foreground dark:hover:bg-accent dark:hover:text-accent-foreground xs:gap-x-3 xs:px-10`,
								glassButtonClass,
							)}
						>
							<div className="flex items-center justify-center">
								<EmailIcon className="h-auto w-8" />
							</div>
							<span className="text-nowrap font-normal">Continue with Email</span>
						</Link>
					)}
					{showOneClickSeparator ? (
						<>
							<div className="flex items-center gap-3 py-1">
								<span className="h-px flex-1 bg-secondary/30" />
								<span className="text-xs font-medium text-muted-foreground">or</span>
								<span className="h-px flex-1 bg-secondary/30" />
							</div>
							<p className="text-center text-xs font-medium text-muted-foreground">
								One-click sign up
							</p>
						</>
					) : null}
					<AuthProviderButton
						title="Google"
						logo={googleIcon}
						onClick={handleGoogleAuth}
						className={glassButtonClass}
						disabled={
							!process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ||
							process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID.includes("REPLACE")
						}
					/>
					<AuthProviderButton
						title="Apple"
						icon={
							<AppleIcon
								className="text-black dark:!text-white"
								style={{ fontSize: 24 }}
								aria-hidden
								inheritViewBox
							/>
						}
						onClick={handleAppleAuth}
						className={glassButtonClass}
						disabled={
							!process.env.NEXT_PUBLIC_APPLE_CLIENT_ID ||
							process.env.NEXT_PUBLIC_APPLE_CLIENT_ID.includes("REPLACE") ||
							process.env.NEXT_PUBLIC_APPLE_CLIENT_ID.includes("REPLACE_WITH")
						}
					/>
				</div>
			</CardContent>

			<CardFooter className="flex justify-center gap-1 py-8 text-center text-xxsm">
				<span className="font-normal text-muted-foreground">Already have an account?</span>
				{loginControl}
			</CardFooter>
		</Card>
	);
}

export type SignupEntryCardProps = SignupEntryInnerProps;

export default function SignupEntryCard(props: SignupEntryCardProps) {
	return (
		<Suspense fallback={<div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>}>
			<SignupEntryInner {...props} />
		</Suspense>
	);
}
